<template>
  <SupportPage class="blog" itemscope itemtype="https://schema.org/BlogPosting">
    <BlogBanner
      slot="top"
      :image="article.img"
      :imageAlt="article.imgAlt"
      :imageHeight="article.imgHeight"
      :imageWidth="article.imgWidth"
      :title="article.title"
      :description="article.description"
    />

    <time itemprop="datePublished" :datetime="article.createdAt" />
    <time itemprop="dateModified" :datetime="article.updatedAt" />

    <Author
      v-if="article.author"
      class="blog-author blog-author--top"
      :author="article.author"
    />

    <Rte ref="contentRef">
      <nuxt-content :document="article" />

      <Author
        v-if="article.author"
        class="blog-author"
        :author="article.author"
      />
    </Rte>
  </SupportPage>
</template>

<script>
import { useScriptTag } from '@vueuse/core';
import { onBeforeUnmount, onMounted, ref } from '@nuxtjs/composition-api';

import BlogBanner from '@/components/blog/blog-banner';
import Rte from '@/components/rte';
import SupportPage from '@/components/support-page';
import Author from '@/components/blog/author';

export default {
  components: {
    BlogBanner,
    Rte,
    SupportPage,
    Author,
  },

  async asyncData({ $content, error, params }) {
    try {
      const article = await $content('articles', params.slug).fetch();

      return { article };
    } catch (e) {
      error({
        statusCode: 404,
        message: 'Page could not be found',
      });
    }
  },

  head() {
    return {
      title: this.article.title,
      meta: [
        {
          hid: 'description',
          name: 'description',
          content: this.article.description,
        },
        // Open Graph
        { hid: 'og:title', property: 'og:title', content: this.article.title },
        {
          hid: 'og:description',
          property: 'og:description',
          content: this.article.description,
        },
        // Twitter Card
        {
          hid: 'twitter:title',
          name: 'twitter:title',
          content: this.article.title,
        },
        {
          hid: 'twitter:description',
          name: 'twitter:description',
          content: this.article.description,
        },
        {
          hid: 'og:image',
          name: 'og:image',
          property: 'og:image',
          content: this.article.img,
        },
        {
          hid: 'og:image:height',
          name: 'og:image:height',
          property: 'og:image:height',
          content: this.article.imgHeight,
        },
        {
          hid: 'og:image:width',
          name: 'og:image:width',
          property: 'og:image:width',
          content: this.article.imgWidth,
        },
        {
          hid: 'twitter:image',
          name: 'twitter:image',
          content: this.article.img,
        },
      ],
    };
  },

  setup(props) {
    const contentRef = ref(null);
    const { load, unload } = useScriptTag(
      'https://platform.twitter.com/widgets.js',
      () => {
        // do something
      },
      { manual: true },
    );

    function formatDate(date) {
      const options = { year: 'numeric', month: 'long', day: 'numeric' };
      return new Date(date).toLocaleDateString('en', options);
    }

    onMounted(() => {
      if (contentRef.value.$el.querySelector('.twitter-tweet')) load();
    });

    onBeforeUnmount(() => {
      unload();
    });

    return {
      formatDate,
      contentRef,
    };
  },
};
</script>

<style lang="scss" scoped>
.blog-author {
  margin-top: 2rem;

  &--top {
    padding-bottom: 1.5rem;
    margin-top: 0;
  }
}
</style>

<style lang="scss">
.icon.icon-link {
  background-image: url('~assets/svg/icon-hashtag.svg');
  display: inline-block;
  width: 20px;
  height: 20px;
  background-size: 20px 20px;
}

.blog .nuxt-content {
  margin-top: -1rem;
}
</style>
